import React from "react";
import { osc, orbitAngle, wobble } from "../../core/AnimationUtils";

/* ================================================================
   Micro Biology — 8 animated templates
   Cell division, virus, bacteria, immune, neuron, DNA replication,
   protein folding, blood cells.
   All motion is a pure function of `t` (seconds since scene start).
   ================================================================ */

// ── 1. Cell Division (Mitosis) ───────────────────────────────────────
export interface CellDivisionProps {
  flowProgress?: number;
  opacity?: number;
  accentColor?: string;
  /** Seconds since the scene started — drives continuous mitosis cycle + organelle jiggle. */
  t?: number;
}
export const CellDivision: React.FC<CellDivisionProps> = ({
  flowProgress,
  opacity = 1,
  accentColor = "#3b82f6",
  t = 0,
}) => {
  // If a caller passes explicit flowProgress, respect it; otherwise drive from t
  // (one full mitosis cycle every 6 s).
  const split = flowProgress !== undefined ? flowProgress : ((t % 6) / 6);
  const offset = split * 80;
  const squeeze = 1 - split * 0.3;
  // Each daughter cell jiggles slightly from the membrane tension.
  const jiggleL = wobble(t, 1.8, 2.5, 0);
  const jiggleR = wobble(t, 2.1, 2.5, 1.2);
  return (
    <svg viewBox="0 0 600 400" style={{ width: "100%", opacity }}>
      <text x="300" y="30" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Cell Division — Mitosis</text>
      {/* Left daughter cell */}
      <ellipse cx={250 - offset + jiggleL} cy="200" rx={80 * squeeze} ry={70 / squeeze} fill="#bfdbfe" stroke={accentColor} strokeWidth="2" opacity={0.5 + split * 0.5} />
      {/* Nucleus left */}
      <circle cx={250 - offset + jiggleL} cy="200" r={20 * squeeze} fill="#1e3a8a" opacity={0.4 + split * 0.6} />
      {/* Chromosomes left */}
      <text x={250 - offset + jiggleL} y="205" textAnchor="middle" fill="#93c5fd" fontSize="12">XX</text>
      {/* Right daughter cell */}
      <ellipse cx={350 + offset + jiggleR} cy="200" rx={80 * squeeze} ry={70 / squeeze} fill="#bfdbfe" stroke={accentColor} strokeWidth="2" opacity={0.5 + split * 0.5} />
      <circle cx={350 + offset + jiggleR} cy="200" r={20 * squeeze} fill="#1e3a8a" opacity={0.4 + split * 0.6} />
      <text x={350 + offset + jiggleR} y="205" textAnchor="middle" fill="#93c5fd" fontSize="12">XX</text>
      {/* Cleavage furrow */}
      {split > 0.2 && split < 0.9 && (
        <line x1="300" y1={200 - 70 / squeeze} x2="300" y2={200 + 70 / squeeze} stroke="#6366f1" strokeWidth="2" strokeDasharray="4 3" opacity={1 - split} />
      )}
      {/* Mitotic spindle fibers */}
      {split < 0.7 && (
        <g opacity={0.3}>
          <line x1={250 - offset} y1="200" x2="300" y2="160" stroke="#94a3b8" strokeWidth="1" />
          <line x1={250 - offset} y1="200" x2="300" y2="240" stroke="#94a3b8" strokeWidth="1" />
          <line x1={350 + offset} y1="200" x2="300" y2="160" stroke="#94a3b8" strokeWidth="1" />
          <line x1={350 + offset} y1="200" x2="300" y2="240" stroke="#94a3b8" strokeWidth="1" />
        </g>
      )}
      {/* Labels */}
      <text x="300" y="340" textAnchor="middle" fill="#94a3b8" fontSize="11">
        {split < 0.3 ? "Prophase" : split < 0.5 ? "Metaphase" : split < 0.7 ? "Anaphase" : "Telophase"}
      </text>
      <text x="300" y="370" textAnchor="middle" fill="#6366f1" fontSize="11">Progress: {Math.round(split * 100)}%</text>
    </svg>
  );
};

// ── 2. Virus Structure ───────────────────────────────────────────────
export interface VirusStructureProps {
  activeIndex?: number;
  opacity?: number;
  accentColor?: string;
  /** Seconds since the scene started — drives virus drift and spike rotation. */
  t?: number;
}
export const VirusStructure: React.FC<VirusStructureProps> = ({
  activeIndex = 0,
  opacity = 1,
  accentColor = "#ef4444",
  t = 0,
}) => {
  const parts = ["Capsid", "Envelope", "Spike Protein", "RNA Core", "Lipid Layer"];
  // Virus drifts slowly in space.
  const dx = wobble(t, 5.5, 6, 0);
  const dy = wobble(t, 4.2, 4, 1.4);
  // Envelope breathes gently.
  const envR = 100 + osc(t, 2.8, -3, 3);
  // Spike proteins wobble outward individually.
  return (
    <svg viewBox="0 0 600 450" style={{ width: "100%", opacity }}>
      <g transform={`translate(${dx},${dy})`}>
        {/* Envelope */}
        <circle cx="300" cy="200" r={envR} fill="#f87171" opacity={activeIndex === 1 ? 0.7 : 0.2} stroke={activeIndex === 1 ? "#fff" : accentColor} strokeWidth={activeIndex === 1 ? 2 : 1} />
        {/* Capsid */}
        <circle cx="300" cy="200" r={envR * 0.7} fill="#fca5a5" opacity={activeIndex === 0 ? 0.8 : 0.3} stroke={activeIndex === 0 ? "#fff" : "#f87171"} strokeWidth={activeIndex === 0 ? 2 : 1} />
        {/* RNA core — gently twisting path */}
        {(() => {
          const twist = wobble(t, 3.0, 8, 0);
          return (
            <path
              d={`M${280 + twist},180 Q300,${160 - twist} ${320 - twist},180 Q300,200 ${280 + twist},180 M290,200 Q310,220 320,200`}
              fill="none"
              stroke={activeIndex === 3 ? "#fff" : "#1e293b"}
              strokeWidth={activeIndex === 3 ? 3 : 2}
            />
          );
        })()}
        {/* Spike proteins — each oscillates at its own phase derived from index */}
        {Array.from({ length: 12 }).map((_, i) => {
          const baseAngle = (i / 12) * Math.PI * 2;
          // Each spike sways by a small amount; period varies by index.
          const spikeWobble = wobble(t, 2.0 + i * 0.15, 0.04, i * 0.52);
          const a = baseAngle + spikeWobble;
          const sx = 300 + envR * Math.cos(a);
          const sy = 200 + envR * Math.sin(a);
          const tipLen = 1.3 + (activeIndex === 2 ? 0.05 * osc(t, 1.2, -1, 1, i * 0.8) : 0);
          const ex = 300 + envR * tipLen * Math.cos(a);
          const ey = 200 + envR * tipLen * Math.sin(a);
          const tipR = activeIndex === 2 ? 4 + osc(t, 1.0, 0, 2, i * 0.6) : 3;
          return (
            <g key={i}>
              <line x1={sx} y1={sy} x2={ex} y2={ey} stroke={activeIndex === 2 ? "#22c55e" : "#6b7280"} strokeWidth={activeIndex === 2 ? 2 : 1} />
              <circle cx={ex} cy={ey} r={tipR} fill={activeIndex === 2 ? "#22c55e" : "#6b7280"} />
            </g>
          );
        })}
        {/* Lipid bilayer dots */}
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (i / 24) * Math.PI * 2;
          return (
            <circle key={`lip-${i}`} cx={300 + envR * 0.95 * Math.cos(a)} cy={200 + envR * 0.95 * Math.sin(a)} r="2" fill={activeIndex === 4 ? "#fbbf24" : "#94a3b8"} opacity={activeIndex === 4 ? 0.8 : 0.3} />
          );
        })}
      </g>
      {/* Labels */}
      <text x="300" y="370" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Virus Structure</text>
      <text x="300" y="395" textAnchor="middle" fill="#f87171" fontSize="12">{parts[activeIndex]}</text>
    </svg>
  );
};

// ── 3. Bacteria Colony ───────────────────────────────────────────────
export interface BacteriaColonyProps {
  phase?: number;
  opacity?: number;
  accentColor?: string;
  /** Seconds since the scene started — drives bacteria jiggle and slow drift. */
  t?: number;
}
export const BacteriaColony: React.FC<BacteriaColonyProps> = ({
  phase: _phase,
  opacity = 1,
  accentColor = "#22c55e",
  t = 0,
}) => {
  // Use t directly; ignore legacy phase to keep determinism.
  const bacteria = Array.from({ length: 12 }).map((_, i) => {
    // Each bacterium has an independent drift derived from its index.
    const bx = wobble(t, 4.0 + i * 0.3, 8, i * 0.52);
    const by = wobble(t, 3.5 + i * 0.25, 6, i * 0.87);
    // Slow rotation — each at slightly different speed.
    const rot = orbitAngle(t, 20 + i * 2, i * 30 * Math.PI / 180) * (180 / Math.PI);
    return {
      cx: 150 + (i % 4) * 130 + bx,
      cy: 120 + Math.floor(i / 4) * 100 + by,
      rx: 20 + (i % 3) * 5,
      ry: 10 + (i % 2) * 4,
      rot,
      color: [accentColor, "#16a34a", "#15803d", "#86efac"][i % 4],
    };
  });
  // Flagella wave phase — advances with t for whipping motion.
  const flagPhase = (t / 0.5) * Math.PI * 2;
  return (
    <svg viewBox="0 0 700 400" style={{ width: "100%", opacity }}>
      {/* Petri dish */}
      <ellipse cx="350" cy="200" rx="280" ry="170" fill="#fef3c7" stroke="#d97706" strokeWidth="2" />
      <ellipse cx="350" cy="200" rx="270" ry="160" fill="#fffbeb" />
      {/* Bacteria */}
      {bacteria.map((b, i) => {
        // Flagella tip oscillates up/down as a pure function of t.
        const flagSwing = 8 * Math.sin(flagPhase + i * 0.9);
        return (
          <g key={i} transform={`rotate(${b.rot},${b.cx},${b.cy})`}>
            <ellipse cx={b.cx} cy={b.cy} rx={b.rx} ry={b.ry} fill={b.color} opacity="0.8" />
            {/* Flagella — computed path, no SMIL */}
            <path
              d={`M${b.cx + b.rx},${b.cy} Q${b.cx + b.rx + 15},${b.cy + flagSwing} ${b.cx + b.rx + 25},${b.cy}`}
              fill="none"
              stroke={b.color}
              strokeWidth="1"
            />
          </g>
        );
      })}
      <text x="350" y="385" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Bacteria Colony</text>
    </svg>
  );
};

// ── 4. Immune Response ───────────────────────────────────────────────
export interface ImmuneResponseProps {
  activeIndex?: number;
  opacity?: number;
  accentColor?: string;
  /** Seconds since the scene started — drives cell pulsing and signal travel. */
  t?: number;
}
export const ImmuneResponse: React.FC<ImmuneResponseProps> = ({
  activeIndex = 0,
  opacity = 1,
  accentColor = "#ec4899",
  t = 0,
}) => {
  const stages = ["Pathogen Enters", "Detection", "T-Cell Attack", "Antibody Release", "Cleanup"];
  // Pathogen drifts slowly into the body.
  const pathX = 150 + wobble(t, 5.0, 8, 0);
  const pathY = 180 + wobble(t, 4.0, 5, 1.1);
  // Macrophage pulses.
  const macroR = 30 + osc(t, 1.5, -3, 3);
  // T-cells orbit the target area.
  const tCellAngles = [0, 1, 2].map(i => orbitAngle(t, 3.0 + i * 0.4, i * (Math.PI * 2 / 3)));
  // Antibodies bob up and down.
  const abY = wobble(t, 2.0, 5, 0);
  return (
    <svg viewBox="0 0 700 400" style={{ width: "100%", opacity }}>
      {/* Body border */}
      <rect x="50" y="50" width="600" height="280" rx="12" fill="#fce7f3" opacity="0.3" stroke={accentColor} strokeWidth="1" />
      {/* Pathogen */}
      <g opacity={activeIndex >= 0 ? 1 : 0.3}>
        <circle cx={pathX} cy={pathY} r="20" fill="#22c55e" stroke="#16a34a" strokeWidth="2" />
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i / 8) * Math.PI * 2;
          // Spikes rotate slowly.
          const spikeA = a + orbitAngle(t, 8, 0);
          return <circle key={i} cx={pathX + 25 * Math.cos(spikeA)} cy={pathY + 25 * Math.sin(spikeA)} r="3" fill="#16a34a" />;
        })}
        <text x={pathX} y={pathY + 40} textAnchor="middle" fill="#22c55e" fontSize="9">Pathogen</text>
      </g>
      {/* Macrophage detection */}
      {activeIndex >= 1 && (
        <g>
          <circle cx="280" cy="160" r={macroR} fill="#6366f1" opacity="0.6" />
          <text x="280" y="165" textAnchor="middle" fill="#fff" fontSize="9">Macro</text>
          <line x1={pathX + 20} y1={pathY} x2="250" y2="168" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="3 2" />
          <text x="280" y="210" textAnchor="middle" fill="#6366f1" fontSize="9">Detected!</text>
        </g>
      )}
      {/* T-cells orbiting the infection site */}
      {activeIndex >= 2 && (
        <g>
          {tCellAngles.map((a, i) => {
            const tx = 380 + 35 * Math.cos(a);
            const ty = 170 + 25 * Math.sin(a);
            return <circle key={i} cx={tx} cy={ty} r="12" fill="#ef4444" opacity="0.7" />;
          })}
          <text x="420" y="220" textAnchor="middle" fill="#ef4444" fontSize="9">T-Cells Attack</text>
        </g>
      )}
      {/* Antibodies — Y shapes that float */}
      {activeIndex >= 3 && (
        <g>
          {[0, 1, 2, 3].map((i) => (
            <text key={i} x={480 + (i % 2) * 30} y={140 + Math.floor(i / 2) * 30 + abY * (i % 2 === 0 ? 1 : -1)} fill="#fbbf24" fontSize="16">Y</text>
          ))}
          <text x="510" y="220" textAnchor="middle" fill="#fbbf24" fontSize="9">Antibodies</text>
        </g>
      )}
      {/* Cleanup checkmark — pulses */}
      {activeIndex >= 4 && (
        <text x="600" y="190" textAnchor="middle" fill="#22c55e" fontSize={12 + osc(t, 1.2, 0, 4)}>✓</text>
      )}
      {/* Stage indicator */}
      <text x="350" y="360" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">{stages[Math.min(activeIndex, stages.length - 1)]}</text>
    </svg>
  );
};

// ── 5. Neuron Firing ─────────────────────────────────────────────────
export interface NeuronFiringProps {
  phase?: number;
  opacity?: number;
  accentColor?: string;
  /** Seconds since the scene started — drives action-potential signal travel along axon. */
  t?: number;
}
export const NeuronFiring: React.FC<NeuronFiringProps> = ({
  phase: _phase,
  opacity = 1,
  accentColor = "#818cf8",
  t = 0,
}) => {
  // Signal travels along the axon; repeats every 2.5 s.
  const signalPos = (t % 2.5) / 2.5;
  // Soma pulses when the signal leaves (signalPos ≈ 0).
  const somaR = 30 + osc(t, 2.5, -3, 3, 0);
  // Signal glow pulses.
  const sigGlow = 8 + osc(t, 0.5, -2, 2);
  // Dendrites sway gently.
  const dendriteSway = wobble(t, 3.5, 4, 0);
  return (
    <svg viewBox="0 0 800 350" style={{ width: "100%", opacity }}>
      {/* Dendrites (left side) */}
      {[-40, -20, 0, 20, 40].map((dy, i) => (
        <line key={i} x1={50 + dendriteSway * (i % 2 === 0 ? 1 : -1)} y1={175 + dy * 2} x2="150" y2={175 + dy} stroke={accentColor} strokeWidth="2" />
      ))}
      {/* Cell body (soma) */}
      <circle cx="180" cy="175" r={somaR} fill="#6366f1" stroke="#4f46e5" strokeWidth="2" />
      <circle cx="180" cy="175" r="12" fill="#4f46e5" />
      <text x="180" y="180" textAnchor="middle" fill="#c7d2fe" fontSize="8">Soma</text>
      {/* Axon */}
      <line x1="210" y1="175" x2="600" y2="175" stroke={accentColor} strokeWidth="4" />
      {/* Myelin sheaths */}
      {[0, 1, 2, 3].map((i) => (
        <rect key={i} x={240 + i * 90} y="160" width="60" height="30" rx="10" fill="#c7d2fe" opacity="0.3" stroke="#6366f1" strokeWidth="1" />
      ))}
      {/* Signal pulse traveling along axon — driven purely by t */}
      <circle cx={210 + signalPos * 390} cy="175" r={sigGlow} fill="#fbbf24" opacity="0.85" />
      {/* Axon terminal / synaptic buttons */}
      {[-15, 0, 15].map((dy, i) => (
        <g key={i}>
          <line x1="600" y1="175" x2="650" y2={170 + dy} stroke={accentColor} strokeWidth="2" />
          <circle cx="660" cy={170 + dy} r="8" fill="#6366f1" />
        </g>
      ))}
      {/* Neurotransmitters released when signal arrives (signalPos > 0.8) */}
      {signalPos > 0.8 && (
        <g>
          {[0, 1, 2, 3].map((i) => {
            // Each vesicle drifts outward from the synapse as a fn of t.
            const driftX = (signalPos - 0.8) / 0.2 * (8 + i * 4);
            const driftY = wobble(t, 0.8 + i * 0.2, 6, i * 1.2);
            return (
              <circle key={i} cx={670 + i * 8 + driftX} cy={165 + (i % 2) * 15 + driftY} r="3" fill="#fbbf24" opacity={0.6 * (1 - (signalPos - 0.8) / 0.2 * 0.5)} />
            );
          })}
        </g>
      )}
      {/* Labels */}
      <text x="80" y="260" fill={accentColor} fontSize="10">Dendrites</text>
      <text x="400" y="220" textAnchor="middle" fill={accentColor} fontSize="10">Axon (myelinated)</text>
      <text x="660" y="220" textAnchor="middle" fill={accentColor} fontSize="10">Synapse</text>
      <text x="400" y="310" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Neuron — Action Potential</text>
    </svg>
  );
};

// ── 6. DNA Replication ───────────────────────────────────────────────
export interface DNAReplicationProps {
  phase?: number;
  opacity?: number;
  accentColor?: string;
  /** Seconds since the scene started — drives replication fork advancement and helicase spin. */
  t?: number;
}
export const DNAReplication: React.FC<DNAReplicationProps> = ({
  phase: _phase,
  opacity = 1,
  accentColor = "#3b82f6",
  t = 0,
}) => {
  // Fork advances rightward; one full sweep every 8 s, then resets.
  const progress = (t % 8) / 8;
  const unzipPoint = 80 + progress * 560;
  // Helicase spins — one revolution per 1.5 s.
  const helicaseAngle = orbitAngle(t, 1.5);
  return (
    <svg viewBox="0 0 700 350" style={{ width: "100%", opacity }}>
      {/* Double helix — left intact strand */}
      {Array.from({ length: 20 }).map((_, i) => {
        const x = 80 + i * 15;
        if (x > unzipPoint) return null;
        const y1 = 150 + Math.sin(i * 0.7) * 30;
        const y2 = 150 - Math.sin(i * 0.7) * 30;
        return (
          <g key={`l-${i}`}>
            <circle cx={x} cy={y1} r="4" fill={accentColor} />
            <circle cx={x} cy={y2} r="4" fill="#ef4444" />
            <line x1={x} y1={y1} x2={x} y2={y2} stroke="#6b7280" strokeWidth="1" opacity="0.4" />
          </g>
        );
      })}
      {/* Replication fork */}
      <line x1={unzipPoint} y1="110" x2={unzipPoint} y2="190" stroke="#fbbf24" strokeWidth="2" strokeDasharray="3 2" />
      <text x={unzipPoint} y="100" textAnchor="middle" fill="#fbbf24" fontSize="9">Fork</text>
      {/* Right unzipped strands — newly synthesized complementary strands appear */}
      {Array.from({ length: 20 }).map((_, i) => {
        const x = unzipPoint + 15 + i * 15;
        if (x > 650) return null;
        const spread = Math.min(i * 3, 40);
        const y1 = 150 + spread + Math.sin(i * 0.7) * 15;
        const y2 = 150 - spread - Math.sin(i * 0.7) * 15;
        // New strands fade in progressively.
        const newOp = Math.min(1, i * 0.12);
        return (
          <g key={`r-${i}`}>
            <circle cx={x} cy={y1} r="4" fill={accentColor} opacity="0.7" />
            <circle cx={x} cy={y1 - 10} r="3" fill="#93c5fd" opacity={newOp * 0.8} />
            <circle cx={x} cy={y2} r="4" fill="#ef4444" opacity="0.7" />
            <circle cx={x} cy={y2 + 10} r="3" fill="#fca5a5" opacity={newOp * 0.8} />
          </g>
        );
      })}
      {/* Helicase enzyme — spins at the fork */}
      <g transform={`rotate(${helicaseAngle * 180 / Math.PI},${unzipPoint},150)`}>
        <circle cx={unzipPoint} cy="150" r="10" fill="#fbbf24" stroke="#f59e0b" strokeWidth="2" />
        {/* Small mark to visualise rotation */}
        <line x1={unzipPoint} y1="141" x2={unzipPoint} y2="145" stroke="#1e293b" strokeWidth="1.5" />
      </g>
      <text x={unzipPoint} y="154" textAnchor="middle" fill="#1e293b" fontSize="7">H</text>
      {/* Legend */}
      <circle cx="80" cy="280" r="5" fill={accentColor} /><text x="95" y="284" fill="#94a3b8" fontSize="9">Template strand</text>
      <circle cx="80" cy="300" r="5" fill="#ef4444" /><text x="95" y="304" fill="#94a3b8" fontSize="9">Complementary strand</text>
      <circle cx="250" cy="280" r="5" fill="#93c5fd" /><text x="265" y="284" fill="#94a3b8" fontSize="9">New strand</text>
      <text x="350" y="340" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">DNA Replication</text>
    </svg>
  );
};

// ── 7. Protein Folding ───────────────────────────────────────────────
export interface ProteinFoldingProps {
  flowProgress?: number;
  opacity?: number;
  accentColor?: string;
  /** Seconds since the scene started — drives continuous fold cycle and bead vibration. */
  t?: number;
}
export const ProteinFolding: React.FC<ProteinFoldingProps> = ({
  flowProgress,
  opacity = 1,
  accentColor = "#8b5cf6",
  t = 0,
}) => {
  // If caller passes explicit flowProgress, respect it; otherwise loop every 5 s.
  const fold = flowProgress !== undefined ? flowProgress : ((t % 5) / 5);
  const numBeads = 15;
  return (
    <svg viewBox="0 0 600 400" style={{ width: "100%", opacity }}>
      <text x="300" y="30" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">Protein Folding</text>
      {/* Amino acid chain */}
      {Array.from({ length: numBeads }).map((_, i) => {
        const lx = 80 + i * 30;
        const ly = 200;
        const angle = (i / numBeads) * Math.PI * 3;
        const foldR = 40 + (i / numBeads) * 50;
        const fx = 300 + foldR * Math.cos(angle);
        const fy = 200 + foldR * Math.sin(angle) * 0.7;
        const x = lx + (fx - lx) * fold;
        const y = ly + (fy - ly) * fold;
        // Each bead vibrates slightly, period varies by index to avoid uniformity.
        const vibX = wobble(t, 1.5 + i * 0.1, 1.5 * fold, i * 0.65);
        const vibY = wobble(t, 1.8 + i * 0.08, 1.0 * fold, i * 0.95);
        const colors = ["#ef4444", accentColor, "#22c55e", "#f59e0b", "#8b5cf6"];
        return (
          <g key={i}>
            {i > 0 && (() => {
              const plx = 80 + (i - 1) * 30;
              const pfx = 300 + (40 + ((i - 1) / numBeads) * 50) * Math.cos(((i - 1) / numBeads) * Math.PI * 3);
              const pfy = 200 + (40 + ((i - 1) / numBeads) * 50) * Math.sin(((i - 1) / numBeads) * Math.PI * 3) * 0.7;
              const px = plx + (pfx - plx) * fold;
              const prevVibX = wobble(t, 1.5 + (i - 1) * 0.1, 1.5 * fold, (i - 1) * 0.65);
              const prevVibY = wobble(t, 1.8 + (i - 1) * 0.08, 1.0 * fold, (i - 1) * 0.95);
              const py = 200 + (pfy - 200) * fold;
              return <line x1={px + prevVibX} y1={py + prevVibY} x2={x + vibX} y2={y + vibY} stroke="#6b7280" strokeWidth="2" />;
            })()}
            <circle cx={x + vibX} cy={y + vibY} r="8" fill={colors[i % colors.length]} stroke="#1e293b" strokeWidth="1" />
          </g>
        );
      })}
      {/* Stage label */}
      <text x="300" y="350" textAnchor="middle" fill="#94a3b8" fontSize="12">
        {fold < 0.3 ? "Primary Structure (chain)" : fold < 0.7 ? "Folding..." : "Tertiary Structure (3D)"}
      </text>
      <text x="300" y="375" textAnchor="middle" fill="#6366f1" fontSize="11">Folding: {Math.round(fold * 100)}%</text>
    </svg>
  );
};

// ── 8. Blood Cells ───────────────────────────────────────────────────
export interface BloodCellsProps {
  phase?: number;
  activeIndex?: number;
  opacity?: number;
  accentColor?: string;
  /** Seconds since the scene started — drives cell flow through the blood vessel. */
  t?: number;
}
export const BloodCells: React.FC<BloodCellsProps> = ({
  phase: _phase,
  activeIndex = 0,
  opacity = 1,
  accentColor = "#ef4444",
  t = 0,
}) => {
  const cellTypes = ["Red Blood Cell", "White Blood Cell", "Platelet"];
  // Flow speed: RBCs travel the 700-unit vessel in ~3 s.
  const flowSpeed = 700 / 3;
  return (
    <svg viewBox="0 0 700 400" style={{ width: "100%", opacity }}>
      {/* Blood vessel walls */}
      <rect x="0" y="100" width="700" height="200" fill="#fca5a5" opacity="0.15" rx="50" />
      <ellipse cx="0" cy="200" rx="30" ry="100" fill="#fca5a5" opacity="0.2" />
      <ellipse cx="700" cy="200" rx="30" ry="100" fill="#fca5a5" opacity="0.2" />
      {/* Flowing red blood cells — each starts at a staggered position */}
      {Array.from({ length: 10 }).map((_, i) => {
        const startOffset = i * 70;
        // x advances with t, wraps around the vessel width.
        const x = ((startOffset + t * flowSpeed) % 700 + 700) % 700;
        const y = 160 + Math.sin(i * 1.3 + (x / 100)) * 40;
        return (
          <ellipse
            key={`rbc-${i}`}
            cx={x}
            cy={y}
            rx="18"
            ry="10"
            fill={activeIndex === 0 ? accentColor : "#fca5a5"}
            opacity={activeIndex === 0 ? 0.8 : 0.4}
            transform={`rotate(${i * 20 + (x / 10)},${x},${y})`}
          />
        );
      })}
      {/* White blood cells — larger, ameboid drift */}
      {[200, 450].map((basex, i) => {
        const wbcX = basex + wobble(t, 5.0 + i * 0.8, 20, i * 1.1);
        const wbcY = 200 + wobble(t, 4.0 + i * 0.6, 15, i * 0.7);
        const wbcR = activeIndex === 1 ? 22 + osc(t, 2.0, -3, 3, i) : 18;
        return (
          <circle
            key={`wbc-${i}`}
            cx={wbcX}
            cy={wbcY}
            r={wbcR}
            fill={activeIndex === 1 ? "#e0e7ff" : "#cbd5e1"}
            opacity={activeIndex === 1 ? 0.8 : 0.3}
            stroke={activeIndex === 1 ? "#6366f1" : "none"}
            strokeWidth="2"
          />
        );
      })}
      {/* Platelets — tiny, spin and flow */}
      {Array.from({ length: 6 }).map((_, i) => {
        const startPlt = i * 110;
        const pltX = ((startPlt + t * flowSpeed * 0.6) % 700 + 700) % 700;
        const pltY = 220 + wobble(t, 3.0 + i * 0.4, 20, i * 1.3);
        const pltRot = orbitAngle(t, 3.0 + i * 0.3, i * 0.6) * (180 / Math.PI);
        return (
          <ellipse
            key={`plt-${i}`}
            cx={pltX}
            cy={pltY}
            rx="6"
            ry="4"
            fill={activeIndex === 2 ? "#fbbf24" : "#d4a574"}
            opacity={activeIndex === 2 ? 0.9 : 0.3}
            transform={`rotate(${pltRot},${pltX},${pltY})`}
          />
        );
      })}
      <text x="350" y="360" textAnchor="middle" fill="#e0e7ff" fontSize="14" fontWeight="bold">{cellTypes[Math.min(activeIndex, cellTypes.length - 1)]}</text>
    </svg>
  );
};
